import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from 'reactstrap';

import Meta from 'components/App/Meta';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Pagination from 'components/Pagination';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Loading from 'components/App/Loading';
import DiscoverMarketDetails from 'components/DiscoverMarketDetails';
import { toast } from 'react-toastify';
import images from 'assets/images';
import { LOCATIONS } from 'constants/index';
import { handleError } from 'utils';
import marketApi from 'api/marketApi';

import styles from './ManagerMarket.module.scss';

const TITLE = 'Quản lý thị trường';

const NAV_TAB = [
  {
    title: 'Tất cả',
    isPublished: undefined,
  },
  {
    title: 'Đã xuất bản',
    isPublished: true,
  },
  {
    title: 'Chưa xuất bản',
    isPublished: false,
  },
];

const breadcrumbList = [
  {
    name: 'Trang chủ',
    isActive: false,
    link: '',
  },
  {
    name: TITLE,
    isActive: true,
    link: LOCATIONS.MANAGER_MARKET,
  },
];

const DEFAULT_LIMIT = 10;

const ManagerMarket = () => {
  const [markets, setMarkets] = useState({ data: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [activeTab, setActiveTab] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);

  const [isOpenCreateMarket, setIsOpenCreateMarket] = useState(false);
  const [editingMarketId, setEditingMarketId] = useState(null);
  const [editingMarketDetails, setEditingMarketDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [deleteMarket, setDeleteMarket] = useState({
    id: '',
    isOpen: false,
    isLoading: false,
  });

  const fetchMarkets = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        page: currentPage,
        limit: DEFAULT_LIMIT,
      };
      if (submittedSearch) {
        payload.q = submittedSearch;
      }
      if (activeTab !== undefined) {
        payload.isPublished = activeTab;
      }
      const response = await marketApi.getMarkets(payload);
      if (response && response.payload) {
        setMarkets(response.payload);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, submittedSearch, activeTab]);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  const handleSearchSubmit = useCallback(() => {
    setSubmittedSearch(search);
    setCurrentPage(1);
  }, [search]);

  const onChangePage = useCallback((selected) => {
    setCurrentPage(selected.selected + 1);
  }, []);

  const onCloseDeleteMarket = useCallback(() => {
    setDeleteMarket((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const onSubmitDeleteMarket = useCallback(async () => {
    try {
      setDeleteMarket((prev) => ({ ...prev, isLoading: true }));
      await marketApi.deleteMarket(deleteMarket.id);
      setDeleteMarket({
        id: '',
        isOpen: false,
        isLoading: false,
      });
      fetchMarkets();
    } catch (error) {
      handleError(error);
      setDeleteMarket((prev) => ({ ...prev, isLoading: false }));
    }
  }, [deleteMarket.id, fetchMarkets]);

  const onChangeTab = useCallback((isPublished) => () => {
    setActiveTab(isPublished);
    setCurrentPage(1);
  }, []);

  const onCloseCreateMarket = useCallback(() => {
    setIsOpenCreateMarket(false);
    setEditingMarketId(null);
    setEditingMarketDetails(null);
  }, []);

  const onOpenCreateMarket = useCallback(() => {
    setEditingMarketId(null);
    setEditingMarketDetails(null);
    setIsOpenCreateMarket(true);
  }, []);

  const onOpenEditMarket = useCallback((id) => () => {
    setEditingMarketId(id);
    setEditingMarketDetails(null);
    setIsOpenCreateMarket(true);
  }, []);

  useEffect(() => {
    if (!editingMarketId) return;
    const fetchDetails = async () => {
      try {
        setIsLoadingDetails(true);
        const response = await marketApi.getMarketDetails(editingMarketId);
        const marketDetails = response?.payload || response;

        if (marketDetails && (marketDetails._id || marketDetails.id)) {
          setEditingMarketDetails(marketDetails);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [editingMarketId]);

  const totalItems = markets.total || 0;
  const totalPage = Math.ceil(totalItems / DEFAULT_LIMIT);
  const paginatedMarkets = markets.data || [];

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name={TITLE} />

        <Container fluid>
          <Breadcrumb pageTitle={TITLE} breadcrumbList={breadcrumbList} />

          <Loading loading={isLoading} loaderOnPage />

          {/* Search Card Section */}
          <Card className="Section">
            <Row>
              <Col md="6">
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="position-relative">
                  <Input
                    placeholder="Tìm kiếm thị trường"
                    style={{ paddingLeft: '36px' }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit();
                      }
                    }}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </Col>

              <Col md="6">
                <div className="d-flex align-items-end h-100 mb-24 mb-md-0">
                  <Button
                    onClick={handleSearchSubmit}
                    style={{ minWidth: '119px' }}
                  >
                    Tìm kiếm
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Table Details Section */}
          <Card className="Section">
            <div className="d-flex flex-column flex-md-row gap-4 justify-content-between custom_pills">
              <Nav pills>
                {NAV_TAB.map((tab) => (
                  <NavItem key={tab.title}>
                    <NavLink
                      className={activeTab === tab.isPublished ? 'active' : ''}
                      onClick={onChangeTab(tab.isPublished)}
                    >
                      <span>{tab.title}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <Button
                classes="Button__Add"
                fontSize="13px"
                type="button"
                onClick={onOpenCreateMarket}
              >
                <i className="fas fa-plus" />
                Thêm thị trường
              </Button>
            </div>

            <Label className="Table__Body__Total">
              {`${totalItems} thị trường`}
            </Label>

            <div className="w-100" style={{ overflow: 'auto' }}>
              <table className="table custom_table">
                <thead>
                  <tr>
                    <th>Thị trường</th>
                    <th>Tỉnh thành phố</th>
                    <th>Tổng chổ nghĩ</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMarkets.map((item) => (
                    <tr key={item._id || item.id} style={{ cursor: 'default' }}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex flex-column gap-1 buttons-group">
                            <div>{item.name}</div>
                            <div>
                              <div className="buttons-action mt-2 d-flex flex-wrap column-gap-3 row-gap-2">
                                <button
                                  type="button"
                                  className="button-action button-destructive"
                                  onClick={() =>
                                    setDeleteMarket({
                                      id: item._id || item.id,
                                      isOpen: true,
                                      isLoading: false,
                                    })
                                  }
                                >
                                  Xoá
                                </button>

                                <button
                                  type="button"
                                  className="button-action button-link text-start"
                                  onClick={onOpenEditMarket(item._id || item.id)}
                                >
                                  Chỉnh sửa
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{item.province || item.region}</td>
                      <td>{item.placeCount ?? item.activeListings ?? 0}</td>
                      <td>
                        {item.isPublished ? (
                          <span className="text-success">Đã xuất bản</span>
                        ) : (
                          <span className="text-danger">Chưa xuất bản</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {paginatedMarkets.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center">
                        Không có dữ liệu
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            page={currentPage}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />
        </Container>
      </div>

      <Modal
        isOpen={isOpenCreateMarket || !!editingMarketId}
        toggle={onCloseCreateMarket}
        size="md"
        fade
        style={{ maxWidth: '779px' }}
      >
        <ModalHeader
          toggle={onCloseCreateMarket}
          close={<AppCloseModalButton onClick={onCloseCreateMarket} />}
          className="justify-content-between"
        >
          {editingMarketId ? 'Chỉnh sửa thị trường' : 'Tạo thị trường'}
        </ModalHeader>

        <ModalBody>
          {editingMarketId && isLoadingDetails && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {editingMarketId
            && !isLoadingDetails
            && (!(editingMarketDetails?._id || editingMarketDetails?.id)
              || (editingMarketDetails._id || editingMarketDetails.id) !== editingMarketId) && (
              <p className="text-center text-danger mb-0">
                Không tải được chi tiết thị trường
              </p>
          )}

          {!editingMarketId && (
            <DiscoverMarketDetails
              isCreate
              callback={() => {
                onCloseCreateMarket();
                setCurrentPage(1);
                fetchMarkets();
                toast.success('Tạo thị trường thành công');
              }}
            />
          )}

          {editingMarketId
            && !isLoadingDetails
            && (editingMarketDetails?._id || editingMarketDetails?.id) === editingMarketId && (
            <DiscoverMarketDetails
              isCreate={false}
              marketId={editingMarketId}
              marketDetails={editingMarketDetails}
              callback={() => {
                onCloseCreateMarket();
                fetchMarkets();
              }}
            />

          )}
        </ModalBody>
      </Modal>

      {/* Standard garbage can delete confirmation modal */}
      <ModalRequestAction
        isOpen={deleteMarket.isOpen}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xoá \nthị trường này không?'}
        onToggle={onCloseDeleteMarket}
        onCloseModal={onCloseDeleteMarket}
        onConfirm={onSubmitDeleteMarket}
      />
    </>
  );
};

export default ManagerMarket;
